import { useContext, useEffect, useState } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import {
  migrateLocalStorageToFirestore,
  clearLocalStorageAlquileres,
  previewLocalStorage,
  enterSandbox,
  exitSandbox,
  resetSandbox,
} from "../../../../application/migrateLocalStorage";
import { isSandboxMode } from "../../../../application/firestoreHelpers";
import {
  FaCloudUploadAlt,
  FaCheckCircle,
  FaTrash,
  FaSpinner,
  FaExclamationTriangle,
  FaFlask,
} from "react-icons/fa";

const ENTITY_LABELS = {
  contratos: "Contratos",
  locadores: "Locadores",
  locatarios: "Locatarios",
  pagos: "Pagos",
  gastos: "Gastos",
  liquidaciones: "Liquidaciones",
  indices: "Indices",
  config: "Configuracion",
};

function DataMigration() {
  const { refreshAll } = useContext(AlquileresContext);
  const [pending, setPending] = useState({});
  const [progress, setProgress] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [sandboxOn, setSandboxOn] = useState(isSandboxMode());
  const [sandboxBusy, setSandboxBusy] = useState(false);
  const [sandboxResult, setSandboxResult] = useState(null);

  const refreshPreview = () => setPending(previewLocalStorage());
  useEffect(() => { refreshPreview(); }, []);

  const handleEnterSandbox = async () => {
    if (!window.confirm(
      "Voy a copiar TODOS los datos actuales de Firestore al sandbox local y activar el modo. " +
      "Despues de esto, todos los cambios (aumentos, cobros, gastos, etc) van a ir al navegador y no van a tocar Firestore. " +
      "¿Continuar?"
    )) return;
    setSandboxBusy(true);
    setSandboxResult(null);
    try {
      const summary = await enterSandbox();
      setSandboxOn(true);
      setSandboxResult({ type: "success", summary });
      // Force a reload so context picks up sandbox data
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      console.error("Sandbox error:", e);
      setSandboxResult({ type: "error", msg: e.message || String(e) });
    } finally {
      setSandboxBusy(false);
    }
  };

  const handleExitSandbox = (clearData) => {
    if (clearData && !window.confirm("¿Borrar tambien los datos del sandbox?")) return;
    exitSandbox({ clearData });
    setSandboxOn(false);
    setSandboxResult(null);
    setTimeout(() => window.location.reload(), 200);
  };

  const handleResetSandbox = () => {
    if (!window.confirm("Borrar todos los datos del sandbox y empezar de cero?")) return;
    resetSandbox();
    setTimeout(() => window.location.reload(), 200);
  };

  const totalPending = Object.values(pending).reduce(
    (s, v) => s + (v?.storage || 0), 0
  );

  const handleMigrate = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    setProgress({});
    try {
      const summary = await migrateLocalStorageToFirestore((evt) => {
        setProgress((p) => ({ ...p, [evt.name]: evt }));
      });
      setResult(summary);
      await refreshAll();
    } catch (e) {
      console.error("Migration error:", e);
      setError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  const handleClearLocal = () => {
    if (!window.confirm("Esto borra todos los datos guardados en este navegador (localStorage). Asegurate de haber subido los datos a Firestore antes. ¿Continuar?")) return;
    setClearing(true);
    try {
      clearLocalStorageAlquileres();
      refreshPreview();
    } finally {
      setClearing(false);
    }
  };

  const summaryRows = result
    ? Object.entries(result).filter(([k]) => ENTITY_LABELS[k])
    : [];

  return (
    <div className="space-y-6 mt-4">
      {/* Sandbox testing */}
      <div
        className={`rounded-xl shadow-sm p-5 space-y-3 ${
          sandboxOn
            ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700"
            : "bg-white dark:bg-gray-800"
        }`}
      >
        <div className="flex items-center gap-2">
          <FaFlask className={`w-5 h-5 ${sandboxOn ? "text-amber-500" : "text-purple-500"}`} />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Modo Sandbox (pruebas)
          </h2>
          {sandboxOn && (
            <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded bg-amber-500 text-white">
              ACTIVO
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Cuando est&aacute; activo, todas las pantallas leen y escriben en una copia local en este
          navegador. Las operaciones (aumentos, cobros, gastos) <strong>no tocan Firestore</strong>.
          &Uacute;til para probar flujos sin riesgo de romper los datos reales.
        </p>

        {!sandboxOn ? (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            <button
              onClick={handleEnterSandbox}
              disabled={sandboxBusy}
              className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {sandboxBusy ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" /> Copiando datos al sandbox...
                </>
              ) : (
                <>
                  <FaFlask className="w-4 h-4" /> Activar Sandbox (copiar Firestore)
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="bg-amber-100 dark:bg-amber-900/40 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
              Estas trabajando sobre una copia local. Lo que cambies aca no se va a guardar en Firestore.
              Cuando termines, hace click en <strong>Salir del sandbox</strong> y vas a volver a ver los datos reales.
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={handleResetSandbox}
                className="inline-flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 px-3 py-1.5 hover:bg-amber-100 dark:hover:bg-amber-900/40 rounded-lg"
              >
                <FaTrash className="w-3 h-3" /> Resetear sandbox
              </button>
              <button
                onClick={() => handleExitSandbox(true)}
                className="inline-flex items-center gap-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium px-4 py-2 rounded-lg"
              >
                Salir y borrar sandbox
              </button>
              <button
                onClick={() => handleExitSandbox(false)}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                <FaCheckCircle className="w-4 h-4" /> Salir del sandbox
              </button>
            </div>
          </div>
        )}

        {sandboxResult?.type === "success" && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2 text-xs text-green-700 dark:text-green-400">
            Sandbox listo. Recargando para que las pantallas tomen los datos copiados...
          </div>
        )}
        {sandboxResult?.type === "error" && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 text-xs text-red-700 dark:text-red-400">
            Error: {sandboxResult.msg}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FaCloudUploadAlt className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Subir datos locales a Firestore
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          La app ahora guarda en Firestore (la nube). Si ya tenés datos cargados localmente
          en este navegador, hacé click en el botón para subirlos. Los IDs se mantienen
          (no se duplica nada). Esta operación es segura: el localStorage no se toca hasta
          que confirmes el botón rojo de abajo.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {Object.entries(ENTITY_LABELS).map(([key, label]) => {
            const count = pending[key]?.storage ?? 0;
            const evt = progress[key];
            const written = result?.[key];
            return (
              <div
                key={key}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
              >
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  {count}
                </p>
                {evt && (
                  <p className="text-[10px] mt-1 text-gray-400">
                    {evt.status === "uploading" && (
                      <span className="text-amber-500 inline-flex items-center gap-1">
                        <FaSpinner className="w-2.5 h-2.5 animate-spin" /> subiendo...
                      </span>
                    )}
                    {evt.status === "done" && (
                      <span className="text-green-500 inline-flex items-center gap-1">
                        <FaCheckCircle className="w-2.5 h-2.5" /> {written ?? evt.count} subidos
                      </span>
                    )}
                    {evt.status === "skipped" && (
                      <span className="text-gray-400">vacío</span>
                    )}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <p className="text-xs text-gray-500">
            Total a subir: <span className="font-semibold">{totalPending}</span> registros
          </p>
          <button
            onClick={handleMigrate}
            disabled={running || totalPending === 0}
            className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {running ? (
              <>
                <FaSpinner className="w-4 h-4 animate-spin" /> Subiendo...
              </>
            ) : (
              <>
                <FaCloudUploadAlt className="w-4 h-4" /> Subir a Firestore
              </>
            )}
          </button>
        </div>

        {result && !error && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm font-medium text-green-700 dark:text-green-400 inline-flex items-center gap-2">
              <FaCheckCircle /> Migración completa
            </p>
            <ul className="text-xs text-green-700 dark:text-green-400 mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
              {summaryRows.map(([k, n]) => (
                <li key={k}>
                  {ENTITY_LABELS[k]}: <span className="font-medium">{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400 inline-flex items-start gap-2">
            <FaExclamationTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Error en la migración</p>
              <p className="text-xs mt-1">{error}</p>
              <p className="text-xs mt-2 text-red-600 dark:text-red-500">
                Si dice "permission-denied", revisá las reglas de Firestore en la consola
                de Firebase. Las colecciones nuevas (alquileres_*) tienen que permitir
                escritura.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Limpieza */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-3 border border-red-100 dark:border-red-900/30">
        <div className="flex items-center gap-2">
          <FaTrash className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Limpiar datos del navegador
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Una vez que confirmes que los datos están bien en Firestore (refrescá la app y
          revisá las pantallas), podés borrar el localStorage para evitar confusiones a futuro.
          <span className="block mt-1 text-amber-600 dark:text-amber-400">
            Solo afecta este navegador. Los datos en Firestore quedan intactos.
          </span>
        </p>
        <div className="flex justify-end">
          <button
            onClick={handleClearLocal}
            disabled={clearing || totalPending === 0}
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FaTrash className="w-4 h-4" />
            {totalPending === 0 ? "localStorage ya está vacío" : "Borrar localStorage"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DataMigration;
